import type { Theme, SxProps, Breakpoint } from '@mui/material/styles';

import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import { useTheme } from '@mui/material/styles';
import ListItemButton from '@mui/material/ListItemButton';
import Drawer, { drawerClasses } from '@mui/material/Drawer';

import { usePathname } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { varAlpha } from 'src/theme/styles';

import { Logo } from 'src/components/logo';
import { Scrollbar } from 'src/components/scrollbar';
import { Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export type NavContentProps = {
  data: {
    path: string;
    title: string;
    icon: React.ReactNode;
    info?: React.ReactNode;
    children?: {
      path: string;
      title: string;
    }[]; // Mảng các mục con nếu có
  }[];
  slots?: {
    topArea?: React.ReactNode;
    bottomArea?: React.ReactNode;
  };
  sx?: SxProps<Theme>;
};

export function NavDesktop({
  sx,
  data,
  slots,
  layoutQuery,
}: NavContentProps & { layoutQuery: Breakpoint }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        pt: 2.5,
        px: 2.5,
        top: 0,
        left: 0,
        height: 1,
        display: 'none',
        position: 'fixed',
        flexDirection: 'column',
        bgcolor: 'var(--layout-nav-bg)',
        zIndex: 'var(--layout-nav-zIndex)',
        width: 'var(--layout-nav-vertical-width)',
        borderRight: `1px solid var(--layout-nav-border-color, ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)})`,
        [theme.breakpoints.up(layoutQuery)]: {
          display: 'flex',
        },
        ...sx,
      }}
    >
      <NavContent data={data} slots={slots} />
    </Box>
  );
}

// ----------------------------------------------------------------------

export function NavMobile({
  sx,
  data,
  open,
  slots,
  onClose
}: NavContentProps & { open: boolean; onClose: () => void }) {
  const pathname = usePathname();

  useEffect(() => {
    if (open) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      sx={{
        [`& .${drawerClasses.paper}`]: {
          pt: 2.5,
          px: 2.5,
          overflow: 'unset',
          bgcolor: 'var(--layout-nav-bg)',
          width: 'var(--layout-nav-mobile-width)',
          ...sx,
        },
      }}
    >
      <NavContent data={data} slots={slots}  />
    </Drawer>
  );
}

export function NavContent({ data, slots, sx }: NavContentProps) {
  const pathname = usePathname();
  const [openItems, setOpenItems] = useState<string[]>([]); // Quản lý trạng thái mở rộng

  const handleToggle = (path: string) => {
    setOpenItems((prev) =>
      prev.includes(path) ? prev.filter((item) => item !== path) : [...prev, path]
    );
  };

  return (
    <>
      <Logo />

      {slots?.topArea}

      <Scrollbar fillContent>
        <Box component="nav" display="flex" flex="1 1 auto" flexDirection="column" sx={sx}>
          <Box component="ul" gap={0.5} display="flex" flexDirection="column">
            {data.map((item) => {
              const isActived = item.path === pathname;
              const isExpanded = openItems.includes(item.path); // Kiểm tra trạng thái mở rộng

              return (
                <Box key={item.title}>
                  <ListItem disableGutters disablePadding>
                    <ListItemButton
                      disableGutters
                      component={RouterLink}
                      href={item.children ? '#' : item.path} // Nếu có children thì không điều hướng
                      onClick={() => item.children && handleToggle(item.path)} // Toggle khi có children
                      sx={{
                        pl: 2,
                        py: 1,
                        gap: 2,
                        pr: 1.5,
                        borderRadius: 0.75,
                        typography: 'body2',
                        fontWeight: 'fontWeightMedium',
                        color: 'var(--layout-nav-item-color)',
                        minHeight: 'var(--layout-nav-item-height)',
                        display: 'flex',
                        alignItems: 'center', 
                        ...(isActived && {
                          fontWeight: 'fontWeightSemiBold',
                          bgcolor: 'var(--layout-nav-item-active-bg)',
                          color: 'var(--layout-nav-item-active-color)',
                          '&:hover': {
                            bgcolor: 'var(--layout-nav-item-hover-bg)',
                          },
                        }),
                      }}
                    >
                      <Box component="span" sx={{ width: 24, height: 24, flexShrink: 0 }} >
                        {item.icon}
                      </Box>

                      <Box component="span" flexGrow={1}  sx={{ whiteSpace: 'normal' }} >
                        {item.title}
                      </Box>

                      {item.children && (
                        <Box component="span" sx={{ ml: 1 }}>
                          {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />} {/* Biểu tượng chỉ báo */}
                        </Box>
                      )}

                      {item.info && item.info}
                    </ListItemButton>
                  </ListItem>

                  {/* Render danh sách con nếu có */}
                  {item.children && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box component="ul" sx={{ pl: 4 }}>
                        {item.children.map((child) => (
                          <ListItem key={child.title} disableGutters disablePadding>
                            <ListItemButton
                              disableGutters
                              component={RouterLink}
                              href={child.path}
                              sx={{
                                pl: 2,
                                py: 0.75,
                                gap: 1,
                                typography: 'body2',
                                fontWeight: 'fontWeightRegular',
                                color: 'var(--layout-nav-item-color)',
                                '&:hover': {
                                  bgcolor: 'var(--layout-nav-item-hover-bg)',
                                },
                              }}
                            >
                              {child.title}
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </Box>
                    </Collapse>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Scrollbar>

      {slots?.bottomArea}
    </>
  );
}
